// Copyright (C) 2021 Bystronic Laser AG

import { SignalrService } from '@/library-services/signalr.service';
import { SpinnerPluginService } from '@/library-services/spinner.service';
import AuthService from '@/services/auth.service';
import { isNil } from '@/utils/misc';
import {
  inject,
  onBeforeMount,
  onBeforeUnmount,
  onMounted,
  Ref,
  shallowRef,
} from 'vue';

export function useSpinner(): SpinnerPluginService {
  const spinner = inject<SpinnerPluginService>('spinner');
  if (isNil(spinner)) {
    throw new Error('SpinnerPluginService is not provided');
  }
  return spinner;
}

export function useAuth(): AuthService {
  const auth = inject<AuthService>('auth');
  if (isNil(auth)) {
    throw new Error('AuthService is not provided');
  }
  return auth;
}

export function useSignalR(): SignalrService {
  const signalr = inject<SignalrService>('signalr');
  if (isNil(signalr)) {
    throw new Error('SignalrService is not provided');
  }
  return signalr;
}

function useAutoRemovedEventListener(
  eventType: string,
  listener: EventListenerOrEventListenerObject,
) {
  onBeforeMount(() => {
    window.addEventListener(eventType, listener);
  });

  onBeforeUnmount(() => {
    window.removeEventListener(eventType, listener);
  });
}

export function useAutoRemovedResizeListener(
  listener: EventListenerOrEventListenerObject,
) {
  useAutoRemovedEventListener('resize', listener);
}

export function useInterval(callback: () => void, intervalSeconds: number) {
  let handle = -1;

  onMounted(() => {
    if (handle === -1) {
      handle = window.setInterval(callback, intervalSeconds * 1000);
    }
  });

  onBeforeUnmount(() => {
    if (handle !== -1) {
      window.clearInterval(handle);
    }
  });
}

export function useAbortController() {
  const controller = shallowRef<AbortController>(new AbortController());

  function abort() {
    controller.value.abort();
    controller.value = new AbortController();
  }

  onBeforeUnmount(() => {
    controller.value.abort();
  });

  return { controller, abort };
}

/**
 * Focuses an element in a modal when opened.
 *
 * As modals are added dynamically to the DOM, the standard HTML `autofocus`
 * attribute doesn't work. It only works when the page loads.
 *
 * @param elementRef Template ref to the element to focus.
 */
export function useModalAutofocusElement(elementRef: Ref) {
  onMounted(() => {
    elementRef.value.focus();
  });
}